/**
 * LostLoanCard - Display a single lost loan with reason categorization
 *
 * Shows loan details and allows setting/editing the loss reason.
 * Supports selection checkbox for bulk operations.
 */

import React, { useState } from 'react';
import { formatCurrency } from '../../utils/formatters';
import {
  LostLoan,
  LossReason,
  LOSS_REASON_LABELS,
  LOSS_REASON_COLORS,
  STANDARD_LOSS_REASONS,
} from '../../hooks/useLostLoans';
import {
  CheckCircle,
  Building2,
  Calendar,
  Clock,
  DollarSign,
  User,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  Phone,
} from 'lucide-react';

interface LostLoanCardProps {
  loan: LostLoan;
  isSelected: boolean;
  onToggleSelect: () => void;
  onSetReason: (loanId: string, reason: LossReason, notes?: string) => Promise<boolean>;
  isSaving: boolean;
  // Contact tracking (optional). The contact UI itself is supplied by the parent
  // as `contactSlot` (canonical <ContactTimeline>); this card only owns the
  // expand/collapse affordance + count badge.
  isContactExpanded?: boolean;
  onToggleContactExpansion?: () => void;
  contactCount?: number;
  contactSlot?: React.ReactNode;
}

const LostLoanCard: React.FC<LostLoanCardProps> = ({
  loan,
  isSelected,
  onToggleSelect,
  onSetReason,
  isSaving,
  isContactExpanded,
  onToggleContactExpansion,
  contactCount,
  contactSlot,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedReason, setSelectedReason] = useState<LossReason>(loan.lossReason || 'unknown');
  const [notes, setNotes] = useState(loan.lossNotes || '');
  const [localSaving, setLocalSaving] = useState(false);
  // Mirror of the setLossReason `notes` cap (services/client-functions/src/refinance/setLossReasonSchema.ts).
  // Keep in sync with that Zod schema; the counter + maxLength stop a save from failing server-side.
  const NOTES_MAX_LENGTH = 2000;

  const hasReason = loan.lossReason !== null && loan.lossReason !== 'unknown';
  // Defensive: fallback to 'unknown' if reason not found in our map
  const reasonColors = (loan.lossReason && LOSS_REASON_COLORS[loan.lossReason])
    ? LOSS_REASON_COLORS[loan.lossReason]
    : LOSS_REASON_COLORS.unknown;

  const handleSave = async () => {
    setLocalSaving(true);
    const success = await onSetReason(loan.loanId, selectedReason, notes || undefined);
    setLocalSaving(false);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setSelectedReason(loan.lossReason || 'unknown');
    setNotes(loan.lossNotes || '');
    setIsEditing(false);
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className={`bg-gray-800 rounded-lg border ${isSelected ? 'border-blue-500 ring-1 ring-blue-500/50' : 'border-gray-700'} overflow-hidden transition-all`}>
      {/* Card Header */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Selection Checkbox */}
          <div className="pt-1">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 cursor-pointer"
            />
          </div>

          <div className="flex-1 min-w-0">
            {/* Customer Name & Reason Badge */}
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <h3 className="text-lg font-medium text-white truncate">
                {loan.customerName}
              </h3>
              {hasReason && (
                <span className={`px-2 py-0.5 rounded text-xs font-medium border ${reasonColors.bg} ${reasonColors.text} ${reasonColors.border}`}>
                  {LOSS_REASON_LABELS[loan.lossReason as LossReason] || loan.lossReason || 'Unknown'}
                </span>
              )}
              {!hasReason && (
                <span className="px-2 py-0.5 rounded text-xs font-medium border bg-yellow-900/30 text-yellow-400 border-yellow-500/30">
                  Needs Categorization
                </span>
              )}
            </div>

            {/* Loan Details */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-400">
              <span className="flex items-center gap-1">
                <span className="text-gray-500">Loan:</span>
                <span className="text-white font-medium">{loan.loanId}</span>
              </span>
              <span className="flex items-center gap-1">
                <Building2 className="w-3.5 h-3.5 text-gray-500" />
                {loan.lender || 'Unknown Lender'}
              </span>
              {loan.balanceAtDischargeCents !== null && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-3.5 h-3.5 text-gray-500" />
                  <span className="text-red-400">{formatCurrency(loan.balanceAtDischargeCents)}</span>
                  <span className="text-gray-500">balance</span>
                </span>
              )}
            </div>

            {/* Additional Details Row */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-400 mt-2">
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5 text-gray-500" />
                Discharged: {formatDate(loan.dischargeDate)}
              </span>
              {loan.monthsActive !== null && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-gray-500" />
                  {loan.monthsActive} months active
                </span>
              )}
              {loan.brokerDisplay && (
                <span className="flex items-center gap-1">
                  <User className="w-3.5 h-3.5 text-gray-500" />
                  {loan.brokerDisplay}
                </span>
              )}
              {loan.officeName && (
                <span className="text-gray-500">
                  {loan.officeName}
                </span>
              )}
            </div>

            {/* Set By Info (if reason is set) */}
            {hasReason && loan.lossReasonSetByEmail && (
              <div className="mt-2 text-xs text-gray-500">
                Set by {loan.lossReasonSetByEmail}
                {loan.lossReasonSetAt && ` on ${formatDate(loan.lossReasonSetAt)}`}
                {loan.lossNotes && (
                  <span className="ml-2 text-gray-400">
                    - "{loan.lossNotes}"
                  </span>
                )}
              </div>
            )}

            {/* Contact History Toggle */}
            {onToggleContactExpansion && (
              <button
                onClick={onToggleContactExpansion}
                className="mt-2 flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
              >
                {isContactExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
                <Phone className="w-3.5 h-3.5" />
                Contact History
                {(contactCount ?? 0) > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 bg-green-900 text-green-300 rounded-full text-[10px] font-medium">
                    {contactCount}
                  </span>
                )}
              </button>
            )}
          </div>

          {/* Balance Display (right side) */}
          {loan.balanceAtDischargeCents !== null && (
            <div className="text-right flex-shrink-0">
              <p className="text-lg font-bold text-red-400">
                {formatCurrency(loan.balanceAtDischargeCents)}
              </p>
              <p className="text-xs text-gray-500">lost balance</p>
            </div>
          )}
        </div>
      </div>

      {/* Reason Edit Section */}
      {!hasReason || isEditing ? (
        <div className="border-t border-gray-700 p-4 bg-gray-850">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1">
              <label className="block text-xs text-gray-400 mb-1">Loss Reason</label>
              <select
                value={selectedReason}
                onChange={(e) => setSelectedReason(e.target.value as LossReason)}
                disabled={localSaving || isSaving}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {STANDARD_LOSS_REASONS.map((reason) => (
                  <option key={reason} value={reason}>
                    {LOSS_REASON_LABELS[reason]}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs text-gray-400 mb-1">Notes (optional)</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={NOTES_MAX_LENGTH}
                placeholder="Add notes about this loss..."
                disabled={localSaving || isSaving}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
              <div
                className={`mt-1 text-right text-[11px] ${
                  notes.length >= NOTES_MAX_LENGTH
                    ? 'text-red-400'
                    : notes.length > NOTES_MAX_LENGTH * 0.9
                      ? 'text-amber-400'
                      : 'text-gray-500'
                }`}
              >
                {notes.length.toLocaleString()}/{NOTES_MAX_LENGTH.toLocaleString()}
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleSave}
              disabled={localSaving || isSaving}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {localSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Save Reason
                </>
              )}
            </button>
            {isEditing && (
              <button
                onClick={handleCancel}
                disabled={localSaving || isSaving}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            )}
          </div>
        </div>
      ) : (
        /* Edit button for existing reasons */
        <div className="border-t border-gray-700 px-4 py-2 bg-gray-850 flex justify-end">
          <button
            onClick={() => setIsEditing(true)}
            className="text-sm text-gray-400 hover:text-white transition-colors"
          >
            Edit Reason
          </button>
        </div>
      )}

      {/* Expanded Contact Section — canonical contact UI supplied by parent */}
      {isContactExpanded && onToggleContactExpansion && contactSlot && (
        <div className="border-t border-gray-700 p-4 bg-gray-900/50">
          {contactSlot}
        </div>
      )}
    </div>
  );
};

export default LostLoanCard;
